import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import type { AnyValue } from "@/lib/rowTypes";

export interface StreakData {
  login_streak: number;
  longest_login_streak: number;
  learning_streak: number;
  longest_learning_streak: number;
  freeze_tokens: number;
  last_login_date: string | null;
  last_learning_date: string | null;
}

export interface CheckInResult {
  already_claimed: boolean;
  login_streak: number;
  longest_login_streak: number;
  freeze_tokens: number;
  xp_bonus: number;
  streak_broken?: boolean;
  streak_frozen?: boolean;
  comeback_bonus?: boolean;
}

export function useStreaks() {
  const { user } = useAuth();
  const [streaks, setStreaks] = useState<StreakData>({
    login_streak: 0,
    longest_login_streak: 0,
    learning_streak: 0,
    longest_learning_streak: 0,
    freeze_tokens: 1,
    last_login_date: null,
    last_learning_date: null,
  });
  const [loading, setLoading] = useState(true);
  const [checkInResult, setCheckInResult] = useState<CheckInResult | null>(null);

  const fetchStreaks = useCallback(async () => {
    if (!user) return;
    const { data } = await supabase
      .from("user_streaks")
      .select("*")
      .eq("user_id", user.id)
      .maybeSingle();

    if (data) {
      const d = data;
      setStreaks({
        login_streak: d.login_streak,
        longest_login_streak: d.longest_login_streak,
        learning_streak: d.learning_streak,
        longest_learning_streak: d.longest_learning_streak,
        freeze_tokens: d.freeze_tokens,
        last_login_date: d.last_login_date,
        last_learning_date: d.last_learning_date,
      });
    }
    setLoading(false);
  }, [user]);

  useEffect(() => {
    fetchStreaks();
  }, [fetchStreaks]);

  const checkInDaily = useCallback(async (): Promise<CheckInResult | null> => {
    if (!user) return null;
    const { data, error } = await supabase.rpc("check_in_daily");
    if (error) {
      console.error("check_in_daily error:", error);
      return null;
    }
    const result = data as AnyValue as CheckInResult;
    setCheckInResult(result);
    setStreaks((prev) => ({
      ...prev,
      login_streak: result.login_streak,
      longest_login_streak: result.longest_login_streak,
      freeze_tokens: result.freeze_tokens,
    }));
    return result;
  }, [user]);

  const recordLearning = useCallback(async () => {
    if (!user) return;
    const { data } = await supabase.rpc("record_learning_activity");
    if (data) {
      const d = data;
      setStreaks((prev) => ({
        ...prev,
        learning_streak: (d as AnyValue).learning_streak,
        longest_learning_streak: (d as AnyValue).longest || prev.longest_learning_streak,
      }));
    }
  }, [user]);

  const clearCheckInResult = useCallback(() => setCheckInResult(null), []);

  return {
    streaks,
    loading,
    checkInResult,
    checkInDaily,
    recordLearning,
    clearCheckInResult,
    refetch: fetchStreaks,
  };
}
